import { getEnvVars } from '@environment';
import { style } from '@styles';
import { ArrowLeft, Mail, Send } from '@tamagui/lucide-icons';
import { Button } from '@unistocks/components/button/button';
import { Form } from '@unistocks/components/form/form';
import { requiredSchema } from '@unistocks/components/form/validators';
import { Page } from '@unistocks/components/page/page';
import { Paragraph } from '@unistocks/components/text/text';
import { AuthContainer } from '@unistocks/features/authentication/auth-container';
import { t } from '@unistocks/shared/localization/index.ts';
import {
  abpTenantFindTenantByName
} from '@unistocks/proxy/axios-client/AbpTenantClient';
import {
  setAccountSendPasswordResetCodeRequestConfig
} from '@unistocks/proxy/axios-client/AccountClient';
import { useAccountSendPasswordResetCodeMutation } from '@unistocks/proxy/axios-client/AccountQuery';
import { TENANT_HEADER } from '@unistocks/shared/consts';
import { Link, useRouter } from 'expo-router';
import { useMemo } from 'react';
import { useMedia } from 'tamagui';
import { z } from 'zod';

const { appName } = getEnvVars();

type ForgotPasswordFormData = {
  email: string;
};

const schemaFactory = () =>
  z.object({
    email: requiredSchema(
      z.email(t('::ThisFieldIsNotAValidEmailAddress.')),
      undefined,
      t('::The {0} field is required.', { 0: t('AbpAccount::EmailAddress') })
    )
  });

export const ForgotPassword: React.FC = () => {
  const router = useRouter();
  const schema = useMemo(() => schemaFactory(), []);
  const media = useMedia();

  const { mutate: accountSendPasswordResetCode, isPending } =
    useAccountSendPasswordResetCodeMutation({
      /* TODO: Show toast */
      onSuccess: () => router.navigate({ pathname: '/(auth)/login', params: { mode: 'form' } })
    });

  const submit = async ({ email }: ForgotPasswordFormData) => {
    const tenantName = email.toLowerCase();

    const tenant = await abpTenantFindTenantByName(tenantName);

    if (tenant?.isActive) {
      setAccountSendPasswordResetCodeRequestConfig({
        headers: { [TENANT_HEADER]: tenant.tenantId }
      });
      accountSendPasswordResetCode({
        appName,
        email,
        returnUrl: null,
        returnUrlHash: null
      });
    }
  };

  return (
    <Page.ScrollView
      headerTransparent
      headerLogo={false}
      headerTitle={media.sm ? undefined : t('AbpAccount::PasswordReset')}
      contentContainerStyle={media.sm ? undefined : style.centerVertical}
    >
      <AuthContainer title={t('AbpAccount::PasswordReset')}>
        <Paragraph mb={'$3'}>{t('AbpAccount::SendPasswordResetLink_Information')}</Paragraph>

        <Form
          onSubmit={submit}
          isLoading={isPending}
          schema={schema}
          triggerText={t('::Submit')}
          triggerIcon={Send}
        >
          <Form.Input
            name={'email'}
            label={t('AbpAccount::EmailAddress')}
            isRequired
            keyboardType={'email-address'}
            textContentType={'username'}
            returnKeyType={'send'}
            autoCapitalize={'none'}
            iconAfter={<Mail />}
          />
        </Form>

        <Link asChild dismissTo href={{ pathname: '/(auth)/login', params: { mode: 'form' } }}>
          <Button variant={'outlined'} Icon={ArrowLeft} mt={'$3'}>
            {t('::Back')}
          </Button>
        </Link>
      </AuthContainer>
    </Page.ScrollView>
  );
};

ForgotPassword.displayName = 'ForgotPassword';
